import { useEffect, useState } from 'react';
import { BoardPane } from '../board/BoardPane';
import { BoardContext, BoardMetaContext, type BoardMeta } from '../board/context';
import type { BoardDoc } from '../doc/BoardDoc';
import { acquireBoard, releaseBoard } from '../doc/docCache';
import { ApiError } from '../sync/api';
import { getBoard } from '../sync/boardsApi';
import { Window } from '../ui/Window';
import { paths } from './router';

/** Opens a board document (local cache + server sync when remote) and renders one pane for it. */
export function BoardPage({ boardId, remote, view }: { boardId: string; remote: boolean; view: string | null }) {
  const [board, setBoard] = useState<BoardDoc | null>(null);
  const [meta, setMeta] = useState<BoardMeta>({ title: remote ? '' : 'Local board', role: remote ? null : 'owner' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const doc = acquireBoard(boardId, remote);
    let alive = true;
    doc.whenReady.then(() => {
      if (alive) setBoard(doc);
    });
    return () => {
      alive = false;
      setBoard(null);
      releaseBoard(doc);
    };
  }, [boardId, remote]);

  useEffect(() => {
    if (!remote) return;
    let alive = true;
    getBoard(boardId)
      .then((b) => {
        if (!alive) return;
        setMeta({ title: b.title, role: b.role });
        document.title = `${b.title} · VDS`;
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof ApiError ? e.message : 'Could not load the board');
      });
    return () => {
      alive = false;
      document.title = 'VDS';
    };
  }, [boardId, remote]);

  if (error) {
    return (
      <main className="desktop">
        <Window title="VDS">
          <p className="bad">{error}</p>
          <p>
            <a className="btn" href={paths.dashboard}>
              Back to boards
            </a>
          </p>
        </Window>
      </main>
    );
  }

  if (!board) {
    return (
      <main className="desktop">
        <Window title="VDS">
          <p>Opening board…</p>
        </Window>
      </main>
    );
  }

  return (
    <BoardContext.Provider value={board}>
      <BoardMetaContext.Provider value={meta}>
        <BoardPane initialView={view} />
      </BoardMetaContext.Provider>
    </BoardContext.Provider>
  );
}
